import { AfterViewInit, Component, ElementRef, viewChild, signal, computed, inject, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { LabelTemplateComponent } from '../../libs/components/label-template/label-template.component';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { CUSTOM_LABEL } from 'src/app/constants/endpoint.const';
import { BaseService } from 'src/app/services/base.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { CustomLabelResponse } from 'src/app/types/response/custom-label.response.type';

@Component({
  selector: 'app-custom-label',
  standalone: false,
  templateUrl: './custom-label.component.html',
  styleUrl: './custom-label.component.scss'
})
export class CustomLabelComponent implements AfterViewInit {
  private sanitizer = inject(DomSanitizer);
  private baseService = inject(BaseService);
  private message = inject(NzMessageService);
  private http = inject(HttpClient);

  skeleton = signal<boolean>(true);
  uploadedImage = signal<string | null>(null);
  photoName = signal<string>('');
  uploadError = signal<string>('');
  isEditMode = signal<boolean>(false);
  fileupload = viewChild<ElementRef<HTMLInputElement>>('fileupload');
  labelTemplate = viewChild<LabelTemplateComponent>('labelTemplate');
  selectedLabel = signal<string>('');
  pdfDataUrl = signal<SafeResourceUrl | null>(null);
  lastPdfBlob = signal<Blob | null>(null);
  isGenerating = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  configId = signal<number | null>(null);
  selectedFile = signal<File | null>(null);

  initialItems = signal<{value: string, checked: boolean}[]>([]);
  initialLogoUrl = signal<string | null>(null);
  deleteLogo = signal<boolean>(false);

  hasChanges = computed(() => {
    const currentItems = this.contentItems();
    const initItems = this.initialItems();

    if (initItems.length > 0) {
      for (const item of currentItems) {
        const initItem = initItems.find(i => i.value === item.value);
        if (initItem && initItem.checked !== item.checked) {
          return true;
        }
      }
    }

    if (this.selectedFile() !== null) {
      return true;
    }

    if (this.initialLogoUrl() !== null && this.uploadedImage() === null) {
      return true;
    }

    return false;
  });

  // Base64 storage for template images to avoid CORS issues in html2canvas
  base64DefaultLogo = signal<string>('');
  base64SapLogo = signal<string>('');
  base64Barcode = signal<string>('');
  base64Watermark = signal<string>('');

  labelList = signal([
    { label: 'A4 (1 Label/Page)', value: 'a4' },
    { label: 'A4 (2 Label/Page)', value: 'a4_2' },
    { label: 'A4 (4 Label/Page)', value: 'a4_4' },
    { label: 'Thermal 1 (10 x 10 cm)', value: 'thermal_1' },
  ]);

  contentItems = signal([
    { label: 'Nilai Asuransi', checked: true, value: 'insurance_value' },
    { label: 'Isi Paket', checked: true, value: 'package_content' },
    { label: 'Kuantitas', checked: true, value: 'quantity' },
    { label: 'Nomor Handphone Pengirim', checked: true, value: 'sender_phone' },
    { label: 'Alamat Pengirim', checked: true, value: 'sender_address' },
    { label: 'Nomor Handphone Penerima', checked: true, value: 'receiver_phone' },
    { label: 'Sensor Nama Penerima', checked: false, value: 'receiver_name' },
    { label: 'Watermark', checked: true, value: 'watermark' },
  ]);

  isWatermarkShow = computed(() => this.contentItems().find(i => i.value === 'watermark')?.checked ?? false);
  isPhoneSenderShow = computed(() => this.contentItems().find(i => i.value === 'sender_phone')?.checked ?? false);
  isAddressSenderShow = computed(() => this.contentItems().find(i => i.value === 'sender_address')?.checked ?? false);
  isPhoneReceiverShow = computed(() => this.contentItems().find(i => i.value === 'receiver_phone')?.checked ?? false);
  isNameReceiverShow = computed(() => this.contentItems().find(i => i.value === 'receiver_name')?.checked ?? false);
  isQuantityShow = computed(() => this.contentItems().find(i => i.value === 'quantity')?.checked ?? false);
  isPackageShow = computed(() => this.contentItems().find(i => i.value === 'package_content')?.checked ?? false);
  isInsuranceShow = computed(() => this.contentItems().find(i => i.value === 'insurance_value')?.checked ?? false);


  constructor() {
    setTimeout(() => this.skeleton.set(false), 1000);
    this.preloadTemplateImages();
  }

  private async preloadTemplateImages() {
    const images = [
      { key: 'default', url: 'https://storage.googleapis.com/komerce/rajaongkir/komship.png' },
      { key: 'sap', url: 'https://storage.googleapis.com/komerce/assets/illustration/sap.png' },
      { key: 'barcode', url: 'https://storage.googleapis.com/komerce/assets/barcode_example.png' },
      { key: 'watermark', url: 'https://storage.googleapis.com/komerce/assets/logo/komship_gray.png' },
    ];

    for (const img of images) {
      try {
        const base64 = await this.convertUrlToBase64(img.url);
        switch (img.key) {
          case 'default': this.base64DefaultLogo.set(base64); break;
          case 'sap': this.base64SapLogo.set(base64); break;
          case 'barcode': this.base64Barcode.set(base64); break;
          case 'watermark': this.base64Watermark.set(base64); break;
        }
      } catch (e) {
        console.error(`Failed to preload ${img.key} image:`, e);
      }
    }
  }

  private convertUrlToBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      // Try direct fetch first
      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        },
        error: (err) => {
          // Fallback to CORS proxy
          const proxyUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`;
          this.http.get(proxyUrl, { responseType: 'blob' }).subscribe({
            next: (proxyBlob) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(proxyBlob);
            },
            error: (proxyErr) => {
              console.error(`Proxy fetch also failed for ${url}`, proxyErr);
              reject(proxyErr);
            }
          });
        }
      });
    });
  }

  toggleEditMode() {
    this.isEditMode.update(v => !v);
  }

  onCheckboxChange() {
    // Timeout gives Angular time to propagate the ngModel change into the getters structure and the template DOM
    this.contentItems.update(items => [...items]);
    setTimeout(() => this.generatePdf(), 100);
  }

  onLabelChange(val: string) {
    this.selectedLabel.set(val);
    // Timeout ensures the DOM resizes properly before capturing the label
    setTimeout(() => this.generatePdf(), 100);
  }

  ngAfterViewInit(): void {
    // Delay slightly to let the hidden template render before capturing
    setTimeout(() => this.generatePdf(), 300);
    this.getCustomLabelConfig();
  }

  triggerFileInput() {
    this.fileupload()?.nativeElement.click();
  }

  getCustomLabelConfig() {
    this.baseService.getData(CUSTOM_LABEL).subscribe((res: { data: CustomLabelResponse }) => {
      if (res?.data) {
        const d = res.data;
        this.configId.set(d.id);
        this.contentItems.update(items =>
          items.map(item => {
            switch (item.value) {
              case 'insurance_value': return { ...item, checked: d.show_insurance_value ?? item.checked };
              case 'package_content': return { ...item, checked: d.show_package_content ?? item.checked };
              case 'quantity': return { ...item, checked: d.show_quantity ?? item.checked };
              case 'sender_phone': return { ...item, checked: d.show_sender_phone ?? item.checked };
              case 'sender_address': return { ...item, checked: d.show_sender_address ?? item.checked };
              case 'receiver_phone': return { ...item, checked: d.show_receiver_phone ?? item.checked };
              case 'receiver_name': return { ...item, checked: d.show_censor_receiver_name ?? item.checked };
              case 'watermark': return { ...item, checked: d.show_watermark ?? item.checked };
              default: return item;
            }
          })
        );

        this.initialItems.set(this.contentItems().map(i => ({ value: i.value, checked: i.checked })));

        if (d.logo_url) {
          this.initialLogoUrl.set(d.logo_url);
          const name = d.logo_url.split('/').pop() || 'logo.png';
          this.photoName.set(name);
          // Convert remote logo to Base64 to avoid CORS issues in htm2canvas
          this.convertUrlToBase64(d.logo_url).then(base64 => {
            this.uploadedImage.set(base64);
            setTimeout(() => this.generatePdf(), 100);
          }).catch(() => {
            this.uploadedImage.set(d.logo_url); // Fallback to raw URL
            setTimeout(() => this.generatePdf(), 100);
          });
        } else {
          this.initialLogoUrl.set(null);
          this.uploadedImage.set(null);
          this.photoName.set('');
          setTimeout(() => this.generatePdf(), 100);
        }
      }
    });
  }

  saveCustomLabelConfig() {
    this.isSaving.set(true);
    const formData = new FormData();
    const items = this.contentItems();

    const getChecked = (val: string) => items.find(i => i.value === val)?.checked ? 1 : 0;

    formData.append('show_insurance_value', getChecked('insurance_value').toString());
    formData.append('show_package_content', getChecked('package_content').toString());
    formData.append('show_quantity', getChecked('quantity').toString());
    formData.append('show_sender_phone', getChecked('sender_phone').toString());
    formData.append('show_sender_address', getChecked('sender_address').toString());
    formData.append('show_receiver_phone', getChecked('receiver_phone').toString());
    formData.append('show_censor_receiver_name', getChecked('receiver_name').toString());
    formData.append('show_watermark', getChecked('watermark').toString());

    if (this.selectedFile()) {
      formData.append('logo', this.selectedFile()!);
    } else if (this.deleteLogo()) {
      formData.append('delete_logo', 'true');
    }

    this.baseService.putDataWithToken(CUSTOM_LABEL, formData).subscribe({
      next: () => {
        this.message.success('Custom label config saved successfully');
        this.isSaving.set(false);
        this.isEditMode.set(false);
        this.selectedFile.set(null);
        this.deleteLogo.set(false);
        this.getCustomLabelConfig();
      },
      error: (err) => {
        this.message.error(err?.error?.meta?.message || 'Failed to save config');
        this.isSaving.set(false);
      }
    });
  }

  handleFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    this.uploadError.set('');
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (file.type !== 'image/png') {
        this.uploadError.set('File must be a PNG image.');
        input.value = '';
        return;
      }

      if (file.size > 1 * 1024 * 1024) {
        this.uploadError.set('File size must not exceed 1MB.');
        input.value = '';
        return;
      }

      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        if (img.width > 140 || img.height > 40) {
          this.uploadError.set(`Image dimensions must be maximum 140×40 px (got ${img.width}×${img.height} px).`);
          input.value = '';
          return;
        }
        this.photoName.set(file.name);
        this.selectedFile.set(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          this.uploadedImage.set(e.target?.result as string);
          setTimeout(() => this.generatePdf(), 100);
        };
        reader.readAsDataURL(file);
      };
      img.src = objectUrl;
    }
  }

  deleteImage() {
    this.uploadedImage.set(null);
    this.photoName.set('');
    this.selectedFile.set(null);
    this.fileupload()!.nativeElement.value = '';
    setTimeout(() => this.generatePdf(), 100);
    this.deleteLogo.set(true);
  }

  async generatePdf(): Promise<void> {
    const templateComp = this.labelTemplate();
    if (!templateComp) return;

    const templateEl = templateComp.el.nativeElement.querySelector('.label-wrapper') as HTMLElement;
    if (!templateEl) return;

    this.isGenerating.set(true);
    this.pdfDataUrl.set(null);

    try {
      const canvas = await html2canvas(templateEl, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: true
      });

      const imgData = canvas.toDataURL('image/png');

      const effectiveLabel = this.selectedLabel() || 'a4';
      let format: string | [number, number] =
        effectiveLabel === 'thermal_1' ? [100, 100] : 'a4';

      const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();

      // Helper: fit canvas image into a slot (slotW x slotH) without stretching
      const fitImage = (x: number, y: number, slotW: number, slotH: number) => {
        const canvasRatio = canvas.width / canvas.height;
        const slotRatio = slotW / slotH;
        let drawW: number, drawH: number;
        if (canvasRatio > slotRatio) {
          // wider than slot → fit by width
          drawW = slotW;
          drawH = slotW / canvasRatio;
        } else {
          // taller than slot → fit by height
          drawH = slotH;
          drawW = slotH * canvasRatio;
        }
        const offsetX = x + (slotW - drawW) / 2;
        const offsetY = y + (slotH - drawH) / 2;
        pdf.addImage(imgData, 'PNG', offsetX, offsetY, drawW, drawH);
      };

      if (effectiveLabel === 'a4_2') {
        fitImage(0, 0, pw, ph / 2);
        fitImage(0, ph / 2, pw, ph / 2);
      } else if (effectiveLabel === 'a4_4') {
        fitImage(0, 0, pw / 2, ph / 2);
        fitImage(pw / 2, 0, pw / 2, ph / 2);
        fitImage(0, ph / 2, pw / 2, ph / 2);
        fitImage(pw / 2, ph / 2, pw / 2, ph / 2);
      } else {
        fitImage(0, 0, pw, ph);
      }

      const blob = pdf.output('blob');
      this.lastPdfBlob.set(blob);
      const objectUrl = URL.createObjectURL(blob);
      this.pdfDataUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl));
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      this.isGenerating.set(false);
    }
  }

  downloadPdf(): void {
    const blob = this.lastPdfBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const timestamp = new Date().getTime();
    a.download = `label_preview_${timestamp}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
}
